import cc from 'classcat';

import styles from './StickyComponent.scss';
import { useStickyComponent } from './use-sticky-component.js';

/**
 * @typedef {object} StickyComponentOptions
 * @property {number} [removalDelayMs] - The number of milliseconds to wait before removing the sticky component from the DOM, to allow for exit animations to complete.
 *  Defaults to 0. We might consider whether we could do this with an animation event listener instead.
 * @property {string} [stickyClassName] - A class name to apply to the sticky component.
 * @property {string} [stickyUnmountingClassName] - A class name to apply to the sticky component when it is unmounting (during the removalDelayMs period)
 * @property {(inView: boolean, stickyComponentContext: { object }) => boolean} [shouldStick] - A function that takes a boolean indicating whether the base component
 *  is within the viewport and the current StickyComponentContext value, and returns a boolean indicating whether the component should be sticky (position fixed, etc.).
 */

/**
 * Provides a wrapper for a component that will make it sticky (position fixed) when it is scrolled out of view or,
 * if provided, when shouldStick returns true for the current inView state and StickyComponentContext value.
 *
 * @param {React.ComponentType} Component
 * @param {StickyComponentOptions} [options]
 * @returns {React.ComponentType}
 */
export function stickyComponent(
  Component,
  {
    stickyClassName,
    stickyUnmountingClassName,
    removalDelayMs,
    shouldStick = (inView) => !inView,
  }
) {
  return ({ className, ...props }) => {
    const { ref, showSticky, stickyUnmounting } = useStickyComponent({
      shouldStick,
      removalDelayMs,
    });

    return (
      <>
        <Component {...props} ref={ref} />
        {showSticky && (
          <Component
            {...props}
            className={cc([
              styles.sticky,
              className,
              stickyClassName,
              { [stickyUnmountingClassName]: stickyUnmounting },
            ])}
          />
        )}
      </>
    );
  };
}
